import { useCampaigns } from "api/campaigns";
import { useExports } from "api/exports";
import { hasModifyAccessToResource } from "api/general";
import { useAppSelector } from "app/hooks";
import { FiltersV2 } from "components/FiltersV2/Filters";
import {
  getNewFiltersOnly,
  parseFiltersForBE,
} from "components/FiltersV2/utils";
import { Subheader } from "components/Header/Subheader/Subheader";
import { SubheaderActionButton } from "components/Header/Subheader/SubheaderActionButton";
import { SubheaderWrapper } from "components/Header/Subheader/SubheaderWrapper";
import { Breadcrumbs, TopHeader } from "components/Header/TopHeader/TopHeader";
import { parseFiltersForExport } from "components/Layout/Export/helpers";
import { OLTable, Row, createHeadItems } from "components/Table/OLTable";
import { calculateCount } from "components/Table/tableHelpers";
import { ConfirmationModal } from "components/Tooltips/ConfirmationModal";
import { useNotifications } from "contexts/Toast/ToastProvider";
import { useMessage } from "hooks/intl";
import { useListing } from "hooks/listing";
import { useCallback, useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { selectTables } from "reduxState/tables/tablesSlice";
import { useTablesStateHelpers } from "state/tables/tableHelpers";
import { REDEMPTION_CODES_LIST_ID } from "state/tables/types";
import { ExportType } from "types/exports";
import {
  SelectedFilter,
  SelectedFilter as SelectedFilterV2,
} from "types/filters";
import { ListingActions } from "types/listing";
import { OLDate } from "utilities/dateFormat";
import { links } from "utilities/links";
import { abbreviateNumber } from "utilities/numbers";
import GenerateCodesModal from "./GenerateCodesModal";
import { filterLabels } from "./helpers";
import { CodeItem } from "./types";

export const CampaignRedemptionCodeList = () => {
  const { useCampaignData, useCampaignRedemptionCodeList } = useCampaigns();
  const { id: campaignID } = useParams<{ id: string }>();
  const { data: campaignData } = useCampaignData(campaignID as string);
  const { message } = useMessage();
  const navigate = useNavigate();
  const { listing, updateListing } = useListing(REDEMPTION_CODES_LIST_ID);
  const tables = useAppSelector(selectTables);
  const { exportList } = useExports();
  const {
    setPersistentFilters,
    setPersistentListingSorting,
    setColumnOrdering,
  } = useTablesStateHelpers();

  const [isGenerateCodesModalVisible, setIsGenerateCodesModalVisible] =
    useState(false);
  const [filters, setFilters] = useState<SelectedFilter[]>(
    tables[REDEMPTION_CODES_LIST_ID]?.filters || [],
  );
  const { notify } = useNotifications();
  const hasModifyExportPermission = hasModifyAccessToResource("EXPORT");
  const [isConfirmationModalOpen, setConfirmationModalOpen] = useState(false);

  const { data: redemptionCodeListData, error } = useCampaignRedemptionCodeList(
    campaignData?.campaignId,
    {
      filters: parseFiltersForBE(getNewFiltersOnly(filters)),
      order: listing.order || "desc",
      page: listing.page,
      rows: listing.rowsPerPage,
      sortKey: listing.property || "createdAt",
    },
  );

  const handleExportList = async () => {
    const filtersObject = parseFiltersForExport(filters);

    const data = await exportList(ExportType.CAMPAIGN_CODE, {
      campaignCode: {
        _orderBy: { createdAt: "desc" },
        _page: listing.page,
        _itemsOnPage: listing.rowsPerPage,
        campaignId: campaignID,
        ...filtersObject,
      },
    });

    if (data) {
      notify(message({ id: "exportSnackbarInfo" }), {
        button: {
          text: message({ id: "viewList" }),
          onClick: ({ closeNotification }) => {
            closeNotification();

            return navigate(links.importsExports, {
              state: { activeTab: "exports" },
            });
          },
        },
      });
      setConfirmationModalOpen(false);
    }
  };

  const createRows = useCallback((): Row[] => {
    return redemptionCodeListData?.items.map((code: CodeItem) => {
      return {
        cells: [
          {
            content: code.code,
            property: "code",
          },
          {
            content: code.status,
            property: "status",
          },
          {
            content: OLDate(code.createdAt),
            property: "createdAt",
          },
          {
            content: OLDate(code.usedAt),
            property: "usedAt",
          },
        ],
        id: code.codeId,
        metadata: {
          usedByMemberId: code.usedByMemberId,
        },
      };
    });
  }, [redemptionCodeListData?.items]);

  const createColumns = useCallback(
    () =>
      createHeadItems([
        {
          headerText: message({ id: "redemptionCode" }),
          property: "code",
          isSortable: true,
        },
        {
          headerText: message({ id: "status" }),
          property: "status",
          isSortable: true,
        },
        {
          headerText: message({ id: "generatedOn" }),
          property: "createdAt",
          isSortable: true,
        },
        {
          headerText: message({ id: "usedOn" }),
          property: "usedAt",
          isSortable: true,
        },
      ]),
    [message],
  );

  const breadcrumbs: Breadcrumbs = {
    parent: {
      label: `${message({ id: "campaign" })} "${campaignData?.name}"`,
      path: links.campaignsList,
    },
  };

  useEffect(() => {
    const isError = typeof error !== "undefined";
    // Save filters only when data is correct and error not occurs
    if (!isError && redemptionCodeListData) {
      setPersistentFilters(filters, REDEMPTION_CODES_LIST_ID);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [filters, redemptionCodeListData, error]);

  return (
    <>
      <GenerateCodesModal
        isOpen={isGenerateCodesModalVisible}
        onClose={() => setIsGenerateCodesModalVisible(false)}
      />
      <TopHeader
        title={
          campaignData &&
          redemptionCodeListData &&
          message(
            { id: "redemptionCodesWithNumber" },
            { codesCount: redemptionCodeListData?.total?.all },
          )
        }
        clearLink={links.campaignsList}
        breadcrumbs={breadcrumbs}
      />
      <Subheader>
        <FiltersV2
          donutLabel={message({ id: "results" })}
          total={redemptionCodeListData?.total}
          savedFilters={getNewFiltersOnly(filters)}
          setFilters={(filters: SelectedFilterV2[]) => {
            listing.page = 1;
            setPersistentListingSorting(
              listing.order,
              listing.property,
              listing.rowsPerPage,
              1,
              REDEMPTION_CODES_LIST_ID,
            );

            setFilters(filters);
          }}
          properties={[
            {
              query: "code",
              label: message({ id: filterLabels.code }),
              type: "text",
            },
            {
              query: "codeId",
              label: message({ id: filterLabels.codeId }),
              type: "text",
            },
            {
              query: "createdAt",
              label: message({ id: filterLabels.createdAt }),
              type: "datetime",
            },
            {
              query: "usedByMemberId",
              label: message({ id: filterLabels.usedByMemberId }),
              type: "text",
            },
            {
              query: "status",
              label: message({ id: filterLabels.status }),
              type: "redemption-code-status",
            },
            {
              query: "usedAt",
              label: message({ id: filterLabels.usedAt }),
              type: "datetime",
            },
          ]}
          data-test-id="redemption-code-list"
        />
        <SubheaderWrapper>
          {hasModifyExportPermission ? (
            <SubheaderActionButton
              text={message(
                { id: "exportToCSVFile" },
                {
                  value: abbreviateNumber(
                    redemptionCodeListData?.total.filtered,
                  ),
                },
              ).toUpperCase()}
              onClick={() => setConfirmationModalOpen(true)}
              data-test-id="redemption-code-list-export-to-csv-button"
            />
          ) : null}
          <SubheaderActionButton
            text={message({ id: "generateCodes" })}
            onClick={() => setIsGenerateCodesModalVisible(true)}
            variant="contained"
            data-test-id="redemption-redemption-code-list-generate-codes-button"
          />
        </SubheaderWrapper>
      </Subheader>
      <OLTable
        fullView
        setColumnOrdering={setColumnOrdering}
        tableName={REDEMPTION_CODES_LIST_ID}
        rows={createRows()}
        columns={createColumns()}
        onSort={(prop) =>
          updateListing({
            payload: prop,
            type: ListingActions.SET_SORT,
          })
        }
        sort={{
          order: listing.order || "desc",
          property: listing.property || "createdAt",
          rowsPerPage: listing.rowsPerPage,
          page: listing.page,
        }}
        actions={{
          body: [
            {
              action: (_id, row) =>
                navigate(
                  links.singleMemberCampaigns(row?.metadata.usedByMemberId),
                ),
              hideCondition: (row) => !row.metadata.usedByMemberId,
              label: message({ id: "viewMemberProfile" }),
            },
          ],
        }}
        pagination={{
          count: calculateCount(
            redemptionCodeListData?.total.filtered,
            redemptionCodeListData?.total.all,
          ),
          rowsPerPage: listing.rowsPerPage,
          page: listing.page,
          onChangePage: (page) => {
            updateListing({ type: ListingActions.CHANGE_PAGE, payload: page });
          },
          onChangeRowsPerPage: (rows) => {
            updateListing({
              type: ListingActions.CHANGE_ROWS_PER_PAGE,
              payload: rows,
            });
          },
        }}
        data-test-id="redemption-code-list"
      />
      <ConfirmationModal
        action={() => handleExportList()}
        close={() => setConfirmationModalOpen(false)}
        header="exportConfirmation"
        info=""
        open={isConfirmationModalOpen}
        cancelText={message({ id: "cancel" })}
        submitText="export"
        modalWidth={454}
        data-test-id={message({ id: "redemption-code-export" })}
      />
    </>
  );
};
